// Connect-time access: the connect sheet, the Connections access row and the batched review
// card (DES3 connect-access DESIGN.md, DES1 verdict with five changes).
//
// Namespace `connect-access`; owner DES3. The 24-language values are DES2's set:
// org/work/DES2/handoff/connect-access-24.json. {service}, {company} and {things} are filled as
// they are, never inflected. Every value holds its placeholders once; counts follow formatCount: the
// number goes outside the forms (changesNoun fills {changes}).
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  connectTitle: 'Connect {service}',
  connectBody:
    'Decide now what Poneva may do with this account. You can change it later in Connections.',
  scopeMe: 'Only me',
  scopeMeBody: 'Only you can use this account. Sharing a chat or Skill doesn’t share it.',
  scopeCompany: 'Everyone at {company}',
  scopeCompanyBody: 'Everyone at {company} can use it. Owners and Admins manage it.',
  readingLabel: 'Reading',
  readingAuto: 'Automatically',
  readingAutoBody: 'Poneva reads {things} without asking. Nothing in {service} changes.',
  readingAsk: 'Ask each time',
  readingAskBody: 'You approve each read.',
  readingList: 'What Poneva can read ({count})',
  readingNone: 'Poneva asks before each use of this account.',
  readOnlyProvider: 'Read-only access',
  changesLabel: 'Changes',
  changesFixed: 'Changes always ask. You approve every change before it happens.',
  continueTo: 'Continue to {service}',
  summary: 'Reads automatically · Changes ask',
  summaryAsk: 'Reads ask · Changes ask',
  reviewNeeded: 'Review reading access',
  cardWants: 'Poneva wants to:',
  cardReadsFooter: 'Nothing in {service} changes.',
  cardChangesFooter: 'Approving lets Poneva make {changes} in {service}.',
  approveCount: 'Approve {n}',
  dontAllow: 'Don’t allow',
  expiresAt: 'Expires {time}',
  rowDetails: 'Details',
  cancel: 'Cancel',
  save: 'Save',
  saving: 'Saving…',
  adminOnly: 'Only Owners and Admins can change this.',
  saveFailed: 'Reading access wasn’t saved. Try again.',
  readingAutoBodyAny: 'Poneva reads from {service} without asking. Nothing in {service} changes.',
  // Before a name is typed in the custom connection form: no placeholder word, never "MCP".
  readingAutoBodyThisServer:
    'Poneva reads from this server without asking. Nothing on this server changes.',
  changesNoun: 'one:change|other:changes',
  tagChange: 'Change',
  tagRead: 'Read',
  rowInclude: 'Include {action}',
  rowWaiting: 'Waiting for you',
  rowRunning: 'Running…',
  rowDone: 'Done',
  rowSkipped: 'Skipped',
  rowDeclined: 'Declined',
  rowExpired: 'Expired',
  rowFailed: 'Didn’t work',
  rowUnknown: 'Couldn’t confirm. It won’t be tried again.',
  outcomeApproved: 'Approved {actions}.',
  actionsNoun: 'one:action|other:actions',
  outcomeDeclined: 'Nothing was sent to {service}.',
  outcomeExpired: 'This request expired. Nothing was sent to {service}.',
  toolReadAuto: 'Read from {service} automatically',
  scopeLabel: 'Who can use it',
  scopeCompanyCaution: 'Only connect an account that’s meant for company use.',
};

export type ConnectAccessCopyKey = keyof typeof en;

const others: Record<string, Record<ConnectAccessCopyKey, string>> = {
  bg: {
    connectTitle: 'Свързване на {service}',
    connectBody:
      'Решете сега какво може да прави Poneva с този акаунт. Можете да промените това по-късно във „Връзки“.',
    scopeMe: 'Само аз',
    scopeMeBody:
      'Само вие можете да използвате този акаунт. Споделянето на чат или Skill не го споделя.',
    scopeCompany: 'Всички в {company}',
    scopeCompanyBody:
      'Всички в {company} могат да го използват. Управлява се от собствениците и администраторите.',
    readingLabel: 'Четене',
    readingAuto: 'Автоматично',
    readingAutoBody: 'Poneva чете {things}, без да пита. Нищо в {service} не се променя.',
    readingAsk: 'Питане всеки път',
    readingAskBody: 'Вие одобрявате всяко четене.',
    readingList: 'Какво може да чете Poneva ({count})',
    readingNone: 'Poneva пита преди всяко използване на този акаунт.',
    readOnlyProvider: 'Достъп само за четене',
    changesLabel: 'Промени',
    changesFixed:
      'За промените винаги се пита. Вие одобрявате всяка промяна, преди да бъде направена.',
    continueTo: 'Продължаване към {service}',
    summary: 'Чете автоматично · Пита за промени',
    summaryAsk: 'Пита за четене · Пита за промени',
    reviewNeeded: 'Прегледайте достъпа за четене',
    cardWants: 'Poneva иска да:',
    cardReadsFooter: 'Нищо в {service} не се променя.',
    cardChangesFooter: 'С одобрението Poneva ще направи {changes} в {service}.',
    approveCount: 'Одобри {n}',
    dontAllow: 'Не разрешавай',
    expiresAt: 'Изтича {time}',
    rowDetails: 'Подробности',
    cancel: 'Отказ',
    save: 'Запази',
    saving: 'Запазване…',
    adminOnly: 'Само собствениците и администраторите могат да променят това.',
    saveFailed: 'Достъпът за четене не беше запазен. Опитайте отново.',
    readingAutoBodyThisServer:
      'Poneva чете от този сървър, без да пита. Нищо на този сървър не се променя.',
    readingAutoBodyAny: 'Poneva чете от {service}, без да пита. Нищо в {service} не се променя.',
    changesNoun: 'one:промяна|other:промени',
    tagChange: 'Промяна',
    tagRead: 'Четене',
    rowInclude: 'Включване: {action}',
    rowWaiting: 'Изчаква вашето решение',
    rowRunning: 'Изпълнява се…',
    rowDone: 'Завършено',
    rowSkipped: 'Пропуснато',
    rowDeclined: 'Отказано',
    rowExpired: 'Изтекло',
    rowFailed: 'Неуспешно',
    rowUnknown: 'Не можа да се потвърди. Няма да се опитва отново.',
    outcomeApproved: 'Одобрено: {actions}.',
    actionsNoun: 'one:действие|other:действия',
    outcomeDeclined: 'Нищо не е изпратено към {service}.',
    outcomeExpired: 'Тази заявка изтече. Нищо не е изпратено към {service}.',
    toolReadAuto: 'Автоматично четене от {service}',
    scopeLabel: 'Кой може да го използва',
    scopeCompanyCaution: 'Свързвайте само акаунт, предназначен за фирмена употреба.',
  },
  cs: {
    connectTitle: 'Připojit službu {service}',
    connectBody:
      'Rozhodněte teď, co smí Poneva s tímto účtem dělat. Později to můžete změnit v sekci Připojení.',
    scopeMe: 'Jen já',
    scopeMeBody: 'Tento účet můžete používat jen vy. Sdílením chatu nebo Skillu se účet nesdílí.',
    scopeCompany: 'Všichni ve firmě {company}',
    scopeCompanyBody:
      'Mohou ho používat všichni ve firmě {company}. Spravují ho vlastníci a správci.',
    readingLabel: 'Čtení',
    readingAuto: 'Automaticky',
    readingAutoBody: 'Poneva čte {things} bez ptaní. Ve službě {service} se nic nezmění.',
    readingAsk: 'Pokaždé se zeptat',
    readingAskBody: 'Každé čtení schvalujete.',
    readingList: 'Co může Poneva číst ({count})',
    readingNone: 'Poneva se zeptá před každým použitím tohoto účtu.',
    readOnlyProvider: 'Přístup jen pro čtení',
    changesLabel: 'Změny',
    changesFixed: 'Na změny se Poneva vždy zeptá. Každou změnu schvalujete dřív, než proběhne.',
    continueTo: 'Pokračovat do služby {service}',
    summary: 'Čte automaticky · Na změny se ptá',
    summaryAsk: 'Na čtení se ptá · Na změny se ptá',
    reviewNeeded: 'Zkontrolujte přístup ke čtení',
    cardWants: 'Poneva chce:',
    cardReadsFooter: 'Ve službě {service} se nic nezmění.',
    cardChangesFooter: 'Schválením umožníte Poneva provést {changes} ve službě {service}.',
    approveCount: 'Schválit {n}',
    dontAllow: 'Nepovolit',
    expiresAt: 'Vyprší {time}',
    rowDetails: 'Podrobnosti',
    cancel: 'Zrušit',
    save: 'Uložit',
    saving: 'Ukládání…',
    adminOnly: 'Změnit to mohou jen vlastníci a správci.',
    saveFailed: 'Přístup ke čtení se neuložil. Zkuste to znovu.',
    readingAutoBodyThisServer:
      'Poneva čte z tohoto serveru bez ptaní. Na tomto serveru se nic nemění.',
    readingAutoBodyAny:
      'Poneva čte ze služby {service} bez ptaní. Ve službě {service} se nic nezmění.',
    changesNoun: 'one:změnu|few:změny|many:změny|other:změn',
    tagChange: 'Změna',
    tagRead: 'Čtení',
    rowInclude: 'Zahrnout: {action}',
    rowWaiting: 'Čeká na vás',
    rowRunning: 'Probíhá…',
    rowDone: 'Hotovo',
    rowSkipped: 'Přeskočeno',
    rowDeclined: 'Odmítnuto',
    rowExpired: 'Platnost vypršela',
    rowFailed: 'Selhalo',
    rowUnknown: 'Nepodařilo se ověřit. Znovu se to nezkusí.',
    outcomeApproved: 'Schváleno: {actions}.',
    actionsNoun: 'one:akce|few:akce|many:akce|other:akcí',
    outcomeDeclined: 'Do služby {service} se nic neodeslalo.',
    outcomeExpired: 'Platnost žádosti vypršela. Do služby {service} se nic neodeslalo.',
    toolReadAuto: 'Automaticky přečteno ze služby {service}',
    scopeLabel: 'Kdo ho může používat',
    scopeCompanyCaution: 'Připojte jen účet určený pro firemní použití.',
  },
  da: {
    connectTitle: 'Forbind {service}',
    connectBody:
      'Bestem nu, hvad Poneva må gøre med denne konto. Du kan ændre det senere i Forbindelser.',
    scopeMe: 'Kun mig',
    scopeMeBody: 'Kun du kan bruge denne konto. Den deles ikke, når du deler en chat eller Skill.',
    scopeCompany: 'Alle i {company}',
    scopeCompanyBody: 'Alle i {company} kan bruge den. Ejere og administratorer administrerer den.',
    readingLabel: 'Læsning',
    readingAuto: 'Automatisk',
    readingAutoBody: 'Poneva læser {things} uden at spørge. Intet i {service} ændres.',
    readingAsk: 'Spørg hver gang',
    readingAskBody: 'Du godkender hver læsning.',
    readingList: 'Hvad Poneva kan læse ({count})',
    readingNone: 'Poneva spørger før hver brug af denne konto.',
    readOnlyProvider: 'Kun læseadgang',
    changesLabel: 'Ændringer',
    changesFixed: 'Poneva spørger altid før ændringer. Du godkender hver ændring, før den sker.',
    continueTo: 'Fortsæt til {service}',
    summary: 'Læser automatisk · Spørger ved ændringer',
    summaryAsk: 'Spørger ved læsning · Spørger ved ændringer',
    reviewNeeded: 'Gennemgå læseadgang',
    cardWants: 'Poneva vil:',
    cardReadsFooter: 'Intet i {service} ændres.',
    cardChangesFooter: 'Godkender du, foretager Poneva {changes} i {service}.',
    approveCount: 'Godkend {n}',
    dontAllow: 'Tillad ikke',
    expiresAt: 'Udløber {time}',
    rowDetails: 'Detaljer',
    cancel: 'Annuller',
    save: 'Gem',
    saving: 'Gemmer…',
    adminOnly: 'Kun ejere og administratorer kan ændre dette.',
    saveFailed: 'Læseadgangen blev ikke gemt. Prøv igen.',
    readingAutoBodyThisServer:
      'Poneva læser fra denne server uden at spørge. Intet på denne server ændres.',
    readingAutoBodyAny: 'Poneva læser fra {service} uden at spørge. Intet i {service} ændres.',
    changesNoun: 'one:ændring|other:ændringer',
    tagChange: 'Ændring',
    tagRead: 'Læsning',
    rowInclude: 'Medtag: {action}',
    rowWaiting: 'Venter på dig',
    rowRunning: 'Kører…',
    rowDone: 'Udført',
    rowSkipped: 'Sprunget over',
    rowDeclined: 'Afvist',
    rowExpired: 'Udløbet',
    rowFailed: 'Mislykket',
    rowUnknown: 'Kunne ikke bekræftes. Det bliver ikke prøvet igen.',
    outcomeApproved: 'Godkendt: {actions}.',
    actionsNoun: 'one:handling|other:handlinger',
    outcomeDeclined: 'Intet blev sendt til {service}.',
    outcomeExpired: 'Anmodningen er udløbet. Intet blev sendt til {service}.',
    toolReadAuto: 'Læst fra {service} automatisk',
    scopeLabel: 'Hvem kan bruge den',
    scopeCompanyCaution: 'Tilknyt kun en konto, der er beregnet til virksomhedsbrug.',
  },
  de: {
    connectTitle: '{service} verbinden',
    connectBody:
      'Legen Sie jetzt fest, was Poneva mit diesem Konto tun darf. Sie können das später unter Verbindungen ändern.',
    scopeMe: 'Nur ich',
    scopeMeBody:
      'Nur Sie können dieses Konto nutzen. Es wird nicht geteilt, wenn Sie einen Chat oder Skill teilen.',
    scopeCompany: 'Alle bei {company}',
    scopeCompanyBody: 'Alle bei {company} können es nutzen. Inhaber und Admins verwalten es.',
    readingLabel: 'Lesen',
    readingAuto: 'Automatisch',
    readingAutoBody: 'Poneva liest {things}, ohne zu fragen. In {service} ändert sich nichts.',
    readingAsk: 'Jedes Mal fragen',
    readingAskBody: 'Sie geben jeden Lesezugriff frei.',
    readingList: 'Was Poneva lesen kann ({count})',
    readingNone: 'Poneva fragt vor jeder Nutzung dieses Kontos.',
    readOnlyProvider: 'Nur Lesezugriff',
    changesLabel: 'Änderungen',
    changesFixed:
      'Bei Änderungen fragt Poneva immer. Sie geben jede Änderung frei, bevor sie erfolgt.',
    continueTo: 'Weiter zu {service}',
    summary: 'Liest automatisch · Fragt bei Änderungen',
    summaryAsk: 'Fragt beim Lesen · Fragt bei Änderungen',
    reviewNeeded: 'Lesezugriff prüfen',
    cardWants: 'Poneva möchte:',
    cardReadsFooter: 'In {service} ändert sich nichts.',
    cardChangesFooter: 'Mit der Genehmigung nimmt Poneva {changes} in {service} vor.',
    approveCount: '{n} genehmigen',
    dontAllow: 'Nicht erlauben',
    expiresAt: 'Läuft ab {time}',
    rowDetails: 'Details',
    cancel: 'Abbrechen',
    save: 'Speichern',
    saving: 'Wird gespeichert…',
    adminOnly: 'Nur Inhaber und Admins können das ändern.',
    saveFailed: 'Der Lesezugriff wurde nicht gespeichert. Versuchen Sie es erneut.',
    readingAutoBodyThisServer:
      'Poneva liest von diesem Server, ohne zu fragen. Auf diesem Server ändert sich nichts.',
    readingAutoBodyAny:
      'Poneva liest aus {service}, ohne zu fragen. In {service} ändert sich nichts.',
    changesNoun: 'one:Änderung|other:Änderungen',
    tagChange: 'Änderung',
    tagRead: 'Lesen',
    rowInclude: 'Einbeziehen: {action}',
    rowWaiting: 'Wartet auf Sie',
    rowRunning: 'Wird ausgeführt…',
    rowDone: 'Erledigt',
    rowSkipped: 'Übersprungen',
    rowDeclined: 'Abgelehnt',
    rowExpired: 'Abgelaufen',
    rowFailed: 'Fehlgeschlagen',
    rowUnknown: 'Konnte nicht bestätigt werden. Es wird nicht erneut versucht.',
    outcomeApproved: 'Genehmigt: {actions}.',
    actionsNoun: 'one:Aktion|other:Aktionen',
    outcomeDeclined: 'An {service} wurde nichts gesendet.',
    outcomeExpired: 'Diese Anfrage ist abgelaufen. An {service} wurde nichts gesendet.',
    toolReadAuto: 'Automatisch aus {service} gelesen',
    scopeLabel: 'Wer es nutzen kann',
    scopeCompanyCaution:
      'Verbinden Sie nur ein Konto, das für die Unternehmensnutzung gedacht ist.',
  },
  el: {
    connectTitle: 'Σύνδεση {service}',
    connectBody:
      'Αποφασίστε τώρα τι μπορεί να κάνει το Poneva με αυτόν τον λογαριασμό. Μπορείτε να το αλλάξετε αργότερα στις Συνδέσεις.',
    scopeMe: 'Μόνο εγώ',
    scopeMeBody:
      'Μόνο εσείς μπορείτε να χρησιμοποιείτε αυτόν τον λογαριασμό. Η κοινοποίηση μιας συνομιλίας ή ενός Skill δεν τον κοινοποιεί.',
    scopeCompany: 'Όλοι στην εταιρεία {company}',
    scopeCompanyBody:
      'Όλοι στην εταιρεία {company} μπορούν να τον χρησιμοποιούν. Τον διαχειρίζονται οι κάτοχοι και οι διαχειριστές.',
    readingLabel: 'Ανάγνωση',
    readingAuto: 'Αυτόματα',
    readingAutoBody:
      'Το Poneva διαβάζει χωρίς να ρωτά: {things}. Τίποτα δεν αλλάζει στο {service}.',
    readingAsk: 'Ερώτηση κάθε φορά',
    readingAskBody: 'Εγκρίνετε κάθε ανάγνωση.',
    readingList: 'Τι μπορεί να διαβάσει το Poneva ({count})',
    readingNone: 'Το Poneva ρωτά πριν από κάθε χρήση αυτού του λογαριασμού.',
    readOnlyProvider: 'Πρόσβαση μόνο για ανάγνωση',
    changesLabel: 'Αλλαγές',
    changesFixed: 'Για τις αλλαγές το Poneva ρωτά πάντα. Εγκρίνετε κάθε αλλαγή πριν γίνει.',
    continueTo: 'Συνέχεια στο {service}',
    summary: 'Διαβάζει αυτόματα · Ρωτά για αλλαγές',
    summaryAsk: 'Ρωτά για ανάγνωση · Ρωτά για αλλαγές',
    reviewNeeded: 'Ελέγξτε την πρόσβαση ανάγνωσης',
    cardWants: 'Το Poneva θέλει να:',
    cardReadsFooter: 'Τίποτα δεν αλλάζει στο {service}.',
    cardChangesFooter: 'Με την έγκριση, το Poneva κάνει {changes} στο {service}.',
    approveCount: 'Έγκριση {n}',
    dontAllow: 'Να μην επιτρέπεται',
    expiresAt: 'Λήγει {time}',
    rowDetails: 'Λεπτομέρειες',
    cancel: 'Ακύρωση',
    save: 'Αποθήκευση',
    saving: 'Αποθήκευση…',
    adminOnly: 'Μόνο οι κάτοχοι και οι διαχειριστές μπορούν να το αλλάξουν.',
    saveFailed: 'Η πρόσβαση ανάγνωσης δεν αποθηκεύτηκε. Δοκιμάστε ξανά.',
    readingAutoBodyThisServer:
      'Το Poneva διαβάζει από αυτόν τον διακομιστή χωρίς να ρωτά. Τίποτα σε αυτόν τον διακομιστή δεν αλλάζει.',
    readingAutoBodyAny:
      'Το Poneva διαβάζει από το {service} χωρίς να ρωτά. Τίποτα δεν αλλάζει στο {service}.',
    changesNoun: 'one:αλλαγή|other:αλλαγές',
    tagChange: 'Αλλαγή',
    tagRead: 'Ανάγνωση',
    rowInclude: 'Συμπερίληψη: {action}',
    rowWaiting: 'Περιμένει εσάς',
    rowRunning: 'Εκτελείται…',
    rowDone: 'Ολοκληρώθηκε',
    rowSkipped: 'Παραλείφθηκε',
    rowDeclined: 'Απορρίφθηκε',
    rowExpired: 'Έληξε',
    rowFailed: 'Απέτυχε',
    rowUnknown: 'Δεν ήταν δυνατή η επιβεβαίωση. Δεν θα γίνει νέα προσπάθεια.',
    outcomeApproved: 'Εγκρίθηκε: {actions}.',
    actionsNoun: 'one:ενέργεια|other:ενέργειες',
    outcomeDeclined: 'Δεν στάλθηκε τίποτα στο {service}.',
    outcomeExpired: 'Αυτό το αίτημα έληξε. Δεν στάλθηκε τίποτα στο {service}.',
    toolReadAuto: 'Αυτόματη ανάγνωση από το {service}',
    scopeLabel: 'Ποιος μπορεί να το χρησιμοποιεί',
    scopeCompanyCaution: 'Συνδέστε μόνο λογαριασμό που προορίζεται για εταιρική χρήση.',
  },
  es: {
    connectTitle: 'Conectar {service}',
    connectBody:
      'Decide ahora qué puede hacer Poneva con esta cuenta. Puedes cambiarlo más tarde en Conexiones.',
    scopeMe: 'Solo yo',
    scopeMeBody: 'Solo tú puedes usar esta cuenta. Compartir un chat o un Skill no la comparte.',
    scopeCompany: 'Todos en {company}',
    scopeCompanyBody:
      'Todos en {company} pueden usarla. La gestionan los propietarios y los administradores.',
    readingLabel: 'Lectura',
    readingAuto: 'Automáticamente',
    readingAutoBody: 'Poneva lee {things} sin preguntar. Nada cambia en {service}.',
    readingAsk: 'Preguntar cada vez',
    readingAskBody: 'Apruebas cada lectura.',
    readingList: 'Qué puede leer Poneva ({count})',
    readingNone: 'Poneva pregunta antes de cada uso de esta cuenta.',
    readOnlyProvider: 'Acceso de solo lectura',
    changesLabel: 'Cambios',
    changesFixed:
      'Para los cambios, Poneva siempre pregunta. Apruebas cada cambio antes de que se haga.',
    continueTo: 'Continuar a {service}',
    summary: 'Lee automáticamente · Pregunta por los cambios',
    summaryAsk: 'Pregunta por las lecturas · Pregunta por los cambios',
    reviewNeeded: 'Revisar el acceso de lectura',
    cardWants: 'Poneva quiere:',
    cardReadsFooter: 'Nada cambia en {service}.',
    cardChangesFooter: 'Si apruebas, Poneva hará {changes} en {service}.',
    approveCount: 'Aprobar {n}',
    dontAllow: 'No permitir',
    expiresAt: 'Caduca {time}',
    rowDetails: 'Detalles',
    cancel: 'Cancelar',
    save: 'Guardar',
    saving: 'Guardando…',
    adminOnly: 'Solo los propietarios y los administradores pueden cambiar esto.',
    saveFailed: 'No se ha guardado el acceso de lectura. Vuelve a intentarlo.',
    readingAutoBodyThisServer:
      'Poneva lee de este servidor sin preguntar. Nada cambia en este servidor.',
    readingAutoBodyAny: 'Poneva lee de {service} sin preguntar. Nada cambia en {service}.',
    changesNoun: 'one:cambio|many:de cambios|other:cambios',
    tagChange: 'Cambio',
    tagRead: 'Lectura',
    rowInclude: 'Incluir: {action}',
    rowWaiting: 'Esperándote',
    rowRunning: 'En curso…',
    rowDone: 'Hecho',
    rowSkipped: 'Omitido',
    rowDeclined: 'Rechazado',
    rowExpired: 'Caducado',
    rowFailed: 'Fallido',
    rowUnknown: 'No se pudo confirmar. No se volverá a intentar.',
    outcomeApproved: 'Aprobado: {actions}.',
    actionsNoun: 'one:acción|many:de acciones|other:acciones',
    outcomeDeclined: 'No se envió nada a {service}.',
    outcomeExpired: 'Esta solicitud caducó. No se envió nada a {service}.',
    toolReadAuto: 'Leído de {service} automáticamente',
    scopeLabel: 'Quién puede usarla',
    scopeCompanyCaution: 'Conecta solo una cuenta pensada para uso de la empresa.',
  },
  et: {
    connectTitle: 'Ühenda teenus {service}',
    connectBody:
      'Otsusta kohe, mida Poneva selle kontoga teha tohib. Saad seda hiljem muuta jaotises Ühendused.',
    scopeMe: 'Ainult mina',
    scopeMeBody:
      'Seda kontot saad kasutada ainult sina. Vestluse või Skilli jagamine seda ei jaga.',
    scopeCompany: 'Kõik ettevõttes {company}',
    scopeCompanyBody:
      'Kõik ettevõttes {company} saavad seda kasutada. Seda haldavad omanikud ja administraatorid.',
    readingLabel: 'Lugemine',
    readingAuto: 'Automaatselt',
    readingAutoBody: 'Poneva loeb küsimata: {things}. Teenuses {service} ei muutu midagi.',
    readingAsk: 'Küsi iga kord',
    readingAskBody: 'Kinnitad iga lugemise.',
    readingList: 'Mida Poneva lugeda saab ({count})',
    readingNone: 'Poneva küsib enne selle konto iga kasutamist.',
    readOnlyProvider: 'Juurdepääs ainult lugemiseks',
    changesLabel: 'Muudatused',
    changesFixed:
      'Muudatuste puhul küsib Poneva alati. Kinnitad iga muudatuse enne, kui see tehakse.',
    continueTo: 'Jätka teenusesse {service}',
    summary: 'Loeb automaatselt · Muudatustel küsib',
    summaryAsk: 'Lugemisel küsib · Muudatustel küsib',
    reviewNeeded: 'Vaata lugemise juurdepääs üle',
    cardWants: 'Poneva soovib:',
    cardReadsFooter: 'Teenuses {service} ei muutu midagi.',
    cardChangesFooter: 'Kinnitamisel teeb Poneva teenuses {service} {changes}.',
    approveCount: 'Kinnita {n}',
    dontAllow: 'Ära luba',
    expiresAt: 'Aegub {time}',
    rowDetails: 'Üksikasjad',
    cancel: 'Loobu',
    save: 'Salvesta',
    saving: 'Salvestamine…',
    adminOnly: 'Seda saavad muuta ainult omanikud ja administraatorid.',
    saveFailed: 'Lugemise juurdepääsu ei salvestatud. Proovi uuesti.',
    readingAutoBodyThisServer:
      'Poneva loeb sellest serverist küsimata. Selles serveris ei muutu midagi.',
    readingAutoBodyAny:
      'Poneva loeb teenusest {service} küsimata. Teenuses {service} ei muutu midagi.',
    changesNoun: 'one:muudatuse|other:muudatust',
    tagChange: 'Muudatus',
    tagRead: 'Lugemine',
    rowInclude: 'Kaasa: {action}',
    rowWaiting: 'Ootab sind',
    rowRunning: 'Töös…',
    rowDone: 'Tehtud',
    rowSkipped: 'Vahele jäetud',
    rowDeclined: 'Keeldutud',
    rowExpired: 'Aegunud',
    rowFailed: 'Ebaõnnestus',
    rowUnknown: 'Ei õnnestunud kinnitada. Uuesti ei proovita.',
    outcomeApproved: 'Kinnitatud: {actions}.',
    actionsNoun: 'one:toiming|other:toimingut',
    outcomeDeclined: 'Teenusesse {service} ei saadetud midagi.',
    outcomeExpired: 'Taotlus aegus. Teenusesse {service} ei saadetud midagi.',
    toolReadAuto: 'Loetud teenusest {service} automaatselt',
    scopeLabel: 'Kes saab seda kasutada',
    scopeCompanyCaution: 'Ühendage ainult konto, mis on mõeldud ettevõtte kasutuseks.',
  },
  fi: {
    connectTitle: 'Yhdistä palvelu {service}',
    connectBody:
      'Päätä nyt, mitä Poneva saa tehdä tällä tilillä. Voit muuttaa sitä myöhemmin Yhteydet-sivulla.',
    scopeMe: 'Vain minä',
    scopeMeBody:
      'Vain sinä voit käyttää tätä tiliä. Keskustelun tai Skillin jakaminen ei jaa sitä.',
    scopeCompany: 'Kaikki organisaatiossa {company}',
    scopeCompanyBody:
      'Kaikki organisaatiossa {company} voivat käyttää sitä. Omistajat ja ylläpitäjät hallinnoivat sitä.',
    readingLabel: 'Lukeminen',
    readingAuto: 'Automaattisesti',
    readingAutoBody: 'Poneva lukee kysymättä: {things}. Palvelussa {service} ei muutu mitään.',
    readingAsk: 'Kysy joka kerta',
    readingAskBody: 'Hyväksyt jokaisen lukemisen.',
    readingList: 'Mitä Poneva voi lukea ({count})',
    readingNone: 'Poneva kysyy ennen jokaista tämän tilin käyttökertaa.',
    readOnlyProvider: 'Vain lukuoikeus',
    changesLabel: 'Muutokset',
    changesFixed:
      'Muutoksista Poneva kysyy aina. Hyväksyt jokaisen muutoksen ennen kuin se tehdään.',
    continueTo: 'Jatka palveluun {service}',
    summary: 'Lukee automaattisesti · Kysyy muutoksista',
    summaryAsk: 'Kysyy lukemisesta · Kysyy muutoksista',
    reviewNeeded: 'Tarkista lukuoikeus',
    cardWants: 'Poneva haluaa:',
    cardReadsFooter: 'Palvelussa {service} ei muutu mitään.',
    cardChangesFooter: 'Kun hyväksyt, Poneva tekee {changes} palveluun {service}.',
    approveCount: 'Hyväksy {n}',
    dontAllow: 'Älä salli',
    expiresAt: 'Vanhenee {time}',
    rowDetails: 'Tiedot',
    cancel: 'Peruuta',
    save: 'Tallenna',
    saving: 'Tallennetaan…',
    adminOnly: 'Vain omistajat ja ylläpitäjät voivat muuttaa tätä.',
    saveFailed: 'Lukuoikeutta ei tallennettu. Yritä uudelleen.',
    readingAutoBodyThisServer:
      'Poneva lukee tästä palvelimesta kysymättä. Tässä palvelimessa ei muutu mikään.',
    readingAutoBodyAny:
      'Poneva lukee palvelusta {service} kysymättä. Palvelussa {service} ei muutu mitään.',
    changesNoun: 'one:muutoksen|other:muutosta',
    tagChange: 'Muutos',
    tagRead: 'Lukeminen',
    rowInclude: 'Sisällytä: {action}',
    rowWaiting: 'Odottaa sinua',
    rowRunning: 'Käynnissä…',
    rowDone: 'Valmis',
    rowSkipped: 'Ohitettu',
    rowDeclined: 'Hylätty',
    rowExpired: 'Vanhentunut',
    rowFailed: 'Epäonnistui',
    rowUnknown: 'Vahvistus ei onnistunut. Tätä ei yritetä uudelleen.',
    outcomeApproved: 'Hyväksytty: {actions}.',
    actionsNoun: 'one:toiminto|other:toimintoa',
    outcomeDeclined: 'Palveluun {service} ei lähetetty mitään.',
    outcomeExpired: 'Pyyntö vanhentui. Palveluun {service} ei lähetetty mitään.',
    toolReadAuto: 'Luettu palvelusta {service} automaattisesti',
    scopeLabel: 'Kuka voi käyttää sitä',
    scopeCompanyCaution: 'Yhdistä vain tili, joka on tarkoitettu yrityskäyttöön.',
  },
  fr: {
    connectTitle: 'Connecter {service}',
    connectBody:
      'Décidez dès maintenant ce que Poneva peut faire avec ce compte. Vous pourrez le modifier plus tard dans Connexions.',
    scopeMe: 'Moi uniquement',
    scopeMeBody:
      'Vous seul pouvez utiliser ce compte. Partager une conversation ou un Skill ne le partage pas.',
    scopeCompany: 'Tout le monde chez {company}',
    scopeCompanyBody:
      'Tout le monde chez {company} peut l’utiliser. Les propriétaires et les administrateurs le gèrent.',
    readingLabel: 'Lecture',
    readingAuto: 'Automatiquement',
    readingAutoBody: 'Poneva lit {things} sans demander. Rien ne change dans {service}.',
    readingAsk: 'Demander à chaque fois',
    readingAskBody: 'Vous approuvez chaque lecture.',
    readingList: 'Ce que Poneva peut lire ({count})',
    readingNone: 'Poneva demande avant chaque utilisation de ce compte.',
    readOnlyProvider: 'Accès en lecture seule',
    changesLabel: 'Modifications',
    changesFixed:
      'Pour les modifications, Poneva demande toujours. Vous approuvez chaque modification avant qu’elle ait lieu.',
    continueTo: 'Continuer vers {service}',
    summary: 'Lit automatiquement · Demande pour les modifications',
    summaryAsk: 'Demande pour la lecture · Demande pour les modifications',
    reviewNeeded: 'Vérifier l’accès en lecture',
    cardWants: 'Poneva souhaite :',
    cardReadsFooter: 'Rien ne change dans {service}.',
    cardChangesFooter:
      'En approuvant, vous permettez à Poneva d’effectuer {changes} dans {service}.',
    approveCount: 'Approuver {n}',
    dontAllow: 'Ne pas autoriser',
    expiresAt: 'Expire {time}',
    rowDetails: 'Détails',
    cancel: 'Annuler',
    save: 'Enregistrer',
    saving: 'Enregistrement…',
    adminOnly: 'Seuls les propriétaires et les administrateurs peuvent modifier ceci.',
    saveFailed: 'L’accès en lecture n’a pas été enregistré. Réessayez.',
    readingAutoBodyThisServer:
      'Poneva lit les données de ce serveur sans demander. Rien ne change sur ce serveur.',
    readingAutoBodyAny: 'Poneva lit dans {service} sans demander. Rien ne change dans {service}.',
    changesNoun: 'one:modification|many:de modifications|other:modifications',
    tagChange: 'Modification',
    tagRead: 'Lecture',
    rowInclude: 'Inclure : {action}',
    rowWaiting: 'En attente de votre réponse',
    rowRunning: 'En cours…',
    rowDone: 'Terminé',
    rowSkipped: 'Ignoré',
    rowDeclined: 'Refusé',
    rowExpired: 'Expiré',
    rowFailed: 'Échec',
    rowUnknown: 'Impossible de confirmer. Aucune nouvelle tentative ne sera faite.',
    outcomeApproved: 'Approuvé : {actions}.',
    actionsNoun: 'one:action|many:d’actions|other:actions',
    outcomeDeclined: 'Rien n’a été envoyé à {service}.',
    outcomeExpired: 'Cette demande a expiré. Rien n’a été envoyé à {service}.',
    toolReadAuto: 'Lu dans {service} automatiquement',
    scopeLabel: 'Qui peut l’utiliser',
    scopeCompanyCaution: 'Ne connectez qu’un compte destiné à un usage professionnel.',
  },
  ga: {
    connectTitle: 'Ceangail {service}',
    connectBody:
      'Socraigh anois cad is féidir le Poneva a dhéanamh leis an gcuntas seo. Is féidir leat é a athrú níos déanaí ar an leathanach Ceangail.',
    scopeMe: 'Mise amháin',
    scopeMeBody:
      'Is tusa amháin a fhéadann an cuntas seo a úsáid. Ní roinntear é nuair a roinneann tú comhrá nó Skill.',
    scopeCompany: 'Gach duine in {company}',
    scopeCompanyBody:
      'Is féidir le gach duine in {company} é a úsáid. Bainistíonn úinéirí agus riarthóirí é.',
    readingLabel: 'Léamh',
    readingAuto: 'Go huathoibríoch',
    readingAutoBody: 'Léann Poneva {things} gan fiafraí. Ní athraítear aon rud in {service}.',
    readingAsk: 'Fiafraigh gach uair',
    readingAskBody: 'Ceadaíonn tú gach léamh.',
    readingList: 'Cad is féidir le Poneva a léamh ({count})',
    readingNone: 'Fiafraíonn Poneva roimh gach úsáid den chuntas seo.',
    readOnlyProvider: 'Rochtain léite amháin',
    changesLabel: 'Athruithe',
    changesFixed:
      'Fiafraíonn Poneva i gcónaí faoi athruithe. Ceadaíonn tú gach athrú sula ndéantar é.',
    continueTo: 'Lean ar aghaidh chuig {service}',
    summary: 'Léann go huathoibríoch · Fiafraíonn faoi athruithe',
    summaryAsk: 'Fiafraíonn faoi léamh · Fiafraíonn faoi athruithe',
    reviewNeeded: 'Athbhreithnigh rochtain léite',
    cardWants: 'Ba mhaith le Poneva:',
    cardReadsFooter: 'Ní athraítear aon rud in {service}.',
    cardChangesFooter: 'Má cheadaíonn tú, déanfaidh Poneva {changes} in {service}.',
    approveCount: 'Ceadaigh {n}',
    dontAllow: 'Ná ceadaigh',
    expiresAt: 'Rachaidh in éag {time}',
    rowDetails: 'Sonraí',
    cancel: 'Cealaigh',
    save: 'Sábháil',
    saving: 'Á shábháil…',
    adminOnly: 'Ní féidir é seo a athrú ach ag úinéirí agus riarthóirí.',
    saveFailed: 'Níor sábháladh an rochtain léite. Bain triail eile as.',
    readingAutoBodyThisServer:
      'Léann Poneva ón bhfreastalaí seo gan fiafraí. Ní athraítear aon rud ar an bhfreastalaí seo.',
    readingAutoBodyAny: 'Léann Poneva ó {service} gan fiafraí. Ní athraítear aon rud in {service}.',
    changesNoun: 'one:athrú|two:athrú|few:athrú|many:athrú|other:athrú',
    tagChange: 'Athrú',
    tagRead: 'Léamh',
    rowInclude: 'Cuir san áireamh: {action}',
    rowWaiting: 'Ag fanacht leat',
    rowRunning: 'Ar siúl…',
    rowDone: 'Déanta',
    rowSkipped: 'Scipeáilte',
    rowDeclined: 'Diúltaithe',
    rowExpired: 'Imithe in éag',
    rowFailed: 'Theip',
    rowUnknown: 'Níorbh fhéidir é a dheimhniú. Ní dhéanfar iarracht eile.',
    outcomeApproved: 'Ceadaithe: {actions}.',
    actionsNoun: 'one:gníomh|two:gníomh|few:gníomh|many:gníomh|other:gníomh',
    outcomeDeclined: 'Níor seoladh aon rud chuig {service}.',
    outcomeExpired: 'D’imigh an t-iarratas seo in éag. Níor seoladh aon rud chuig {service}.',
    toolReadAuto: 'Léadh ó {service} go huathoibríoch',
    scopeLabel: 'Cé a fhéadann é a úsáid',
    scopeCompanyCaution: 'Ná ceangail ach cuntas atá ceaptha d’úsáid na cuideachta.',
  },
  hr: {
    connectTitle: 'Poveži uslugu {service}',
    connectBody:
      'Odlučite sada što Poneva smije raditi s ovim računom. Kasnije to možete promijeniti u odjeljku Veze.',
    scopeMe: 'Samo ja',
    scopeMeBody:
      'Samo vi možete koristiti ovaj račun. Dijeljenjem razgovora ili Skilla on se ne dijeli.',
    scopeCompany: 'Svi u tvrtki {company}',
    scopeCompanyBody:
      'Svi u tvrtki {company} mogu ga koristiti. Njime upravljaju vlasnici i administratori.',
    readingLabel: 'Čitanje',
    readingAuto: 'Automatski',
    readingAutoBody: 'Poneva bez pitanja čita: {things}. U usluzi {service} ništa se ne mijenja.',
    readingAsk: 'Pitaj svaki put',
    readingAskBody: 'Odobravate svako čitanje.',
    readingList: 'Što Poneva može čitati ({count})',
    readingNone: 'Poneva pita prije svake upotrebe ovog računa.',
    readOnlyProvider: 'Pristup samo za čitanje',
    changesLabel: 'Promjene',
    changesFixed:
      'Za promjene Poneva uvijek pita. Svaku promjenu odobravate prije nego što se dogodi.',
    continueTo: 'Nastavi na uslugu {service}',
    summary: 'Čita automatski · Pita za promjene',
    summaryAsk: 'Pita za čitanje · Pita za promjene',
    reviewNeeded: 'Pregledajte pristup za čitanje',
    cardWants: 'Poneva želi:',
    cardReadsFooter: 'U usluzi {service} ništa se ne mijenja.',
    cardChangesFooter: 'Odobravanjem dopuštate da Poneva napravi {changes} u usluzi {service}.',
    approveCount: 'Odobri {n}',
    dontAllow: 'Ne dopusti',
    expiresAt: 'Istječe {time}',
    rowDetails: 'Pojedinosti',
    cancel: 'Odustani',
    save: 'Spremi',
    saving: 'Spremanje…',
    adminOnly: 'Ovo mogu promijeniti samo vlasnici i administratori.',
    saveFailed: 'Pristup za čitanje nije spremljen. Pokušajte ponovno.',
    readingAutoBodyThisServer:
      'Poneva čita s ovog poslužitelja bez pitanja. Na ovom poslužitelju ništa se ne mijenja.',
    readingAutoBodyAny:
      'Poneva bez pitanja čita iz usluge {service}. U usluzi {service} ništa se ne mijenja.',
    changesNoun: 'one:promjenu|few:promjene|other:promjena',
    tagChange: 'Promjena',
    tagRead: 'Čitanje',
    rowInclude: 'Uključi: {action}',
    rowWaiting: 'Čeka vas',
    rowRunning: 'U tijeku…',
    rowDone: 'Gotovo',
    rowSkipped: 'Preskočeno',
    rowDeclined: 'Odbijeno',
    rowExpired: 'Isteklo',
    rowFailed: 'Neuspješno',
    rowUnknown: 'Nije moguće potvrditi. Neće se ponovno pokušati.',
    outcomeApproved: 'Odobreno: {actions}.',
    actionsNoun: 'one:radnja|few:radnje|other:radnji',
    outcomeDeclined: 'Ništa nije poslano u uslugu {service}.',
    outcomeExpired: 'Ovaj zahtjev je istekao. Ništa nije poslano u uslugu {service}.',
    toolReadAuto: 'Automatski pročitano iz usluge {service}',
    scopeLabel: 'Tko ga može koristiti',
    scopeCompanyCaution: 'Povežite samo račun namijenjen za poslovnu upotrebu.',
  },
  hu: {
    connectTitle: '{service} csatlakoztatása',
    connectBody:
      'Döntse el most, mit tehet a Poneva ezzel a fiókkal. Később módosíthatja a Kapcsolatok oldalon.',
    scopeMe: 'Csak én',
    scopeMeBody:
      'Ezt a fiókot csak Ön használhatja. Egy csevegés vagy Skill megosztása nem osztja meg.',
    scopeCompany: 'Mindenki itt: {company}',
    scopeCompanyBody:
      'Mindenki használhatja itt: {company}. A tulajdonosok és az adminisztrátorok kezelik.',
    readingLabel: 'Olvasás',
    readingAuto: 'Automatikusan',
    readingAutoBody:
      'A Poneva kérdezés nélkül olvassa: {things}. A(z) {service} szolgáltatásban semmi sem változik.',
    readingAsk: 'Kérdezés minden alkalommal',
    readingAskBody: 'Minden olvasást Ön hagy jóvá.',
    readingList: 'Mit olvashat a Poneva ({count})',
    readingNone: 'A Poneva a fiók minden használata előtt rákérdez.',
    readOnlyProvider: 'Csak olvasási hozzáférés',
    changesLabel: 'Változások',
    changesFixed:
      'Változtatás előtt a Poneva mindig rákérdez. Minden változtatást Ön hagy jóvá, mielőtt megtörténne.',
    continueTo: 'Tovább ide: {service}',
    summary: 'Automatikusan olvas · Változtatásnál kérdez',
    summaryAsk: 'Olvasásnál kérdez · Változtatásnál kérdez',
    reviewNeeded: 'Olvasási hozzáférés ellenőrzése',
    cardWants: 'A Poneva ezt szeretné:',
    cardReadsFooter: 'A(z) {service} szolgáltatásban semmi sem változik.',
    cardChangesFooter: 'Jóváhagyással a Poneva {changes} végez itt: {service}.',
    approveCount: '{n} jóváhagyása',
    dontAllow: 'Ne engedélyezze',
    expiresAt: 'Lejár {time}',
    rowDetails: 'Részletek',
    cancel: 'Mégse',
    save: 'Mentés',
    saving: 'Mentés…',
    adminOnly: 'Ezt csak a tulajdonosok és az adminisztrátorok módosíthatják.',
    saveFailed: 'Az olvasási hozzáférés nem lett mentve. Próbálja újra.',
    readingAutoBodyThisServer:
      'A Poneva kérdezés nélkül olvas erről a kiszolgálóról. Ezen a kiszolgálón semmi sem változik.',
    readingAutoBodyAny:
      'A Poneva kérdezés nélkül olvas a(z) {service} szolgáltatásból. A(z) {service} szolgáltatásban semmi sem változik.',
    changesNoun: 'one:változtatást|other:változtatást',
    tagChange: 'Változtatás',
    tagRead: 'Olvasás',
    rowInclude: 'Belefoglalás: {action}',
    rowWaiting: 'Önre vár',
    rowRunning: 'Fut…',
    rowDone: 'Kész',
    rowSkipped: 'Kihagyva',
    rowDeclined: 'Elutasítva',
    rowExpired: 'Lejárt',
    rowFailed: 'Sikertelen',
    rowUnknown: 'Nem sikerült megerősíteni. Nem történik újabb próbálkozás.',
    outcomeApproved: 'Jóváhagyva: {actions}.',
    actionsNoun: 'one:művelet|other:művelet',
    outcomeDeclined: 'A(z) {service} szolgáltatásba semmi sem lett elküldve.',
    outcomeExpired: 'Ez a kérés lejárt. A(z) {service} szolgáltatásba semmi sem lett elküldve.',
    toolReadAuto: 'Automatikus olvasás a(z) {service} szolgáltatásból',
    scopeLabel: 'Ki használhatja',
    scopeCompanyCaution: 'Csak céges használatra szánt fiókot kapcsoljon össze.',
  },
  it: {
    connectTitle: 'Collega {service}',
    connectBody:
      'Decidi ora cosa può fare Poneva con questo account. Puoi cambiarlo più tardi in Connessioni.',
    scopeMe: 'Solo io',
    scopeMeBody:
      'Solo tu puoi usare questo account. Condividere una chat o una Skill non lo condivide.',
    scopeCompany: 'Tutti in {company}',
    scopeCompanyBody:
      'Tutti in {company} possono usarlo. Lo gestiscono i proprietari e gli amministratori.',
    readingLabel: 'Lettura',
    readingAuto: 'Automaticamente',
    readingAutoBody: 'Poneva legge {things} senza chiedere. In {service} non cambia nulla.',
    readingAsk: 'Chiedi ogni volta',
    readingAskBody: 'Approvi ogni lettura.',
    readingList: 'Cosa può leggere Poneva ({count})',
    readingNone: 'Poneva chiede prima di ogni uso di questo account.',
    readOnlyProvider: 'Accesso in sola lettura',
    changesLabel: 'Modifiche',
    changesFixed: 'Per le modifiche Poneva chiede sempre. Approvi ogni modifica prima che avvenga.',
    continueTo: 'Continua su {service}',
    summary: 'Legge automaticamente · Chiede per le modifiche',
    summaryAsk: 'Chiede per la lettura · Chiede per le modifiche',
    reviewNeeded: 'Rivedi l’accesso in lettura',
    cardWants: 'Poneva vuole:',
    cardReadsFooter: 'In {service} non cambia nulla.',
    cardChangesFooter: 'Approvando, Poneva farà {changes} in {service}.',
    approveCount: 'Approva {n}',
    dontAllow: 'Non consentire',
    expiresAt: 'Scade {time}',
    rowDetails: 'Dettagli',
    cancel: 'Annulla',
    save: 'Salva',
    saving: 'Salvataggio…',
    adminOnly: 'Solo i proprietari e gli amministratori possono modificarlo.',
    saveFailed: 'L’accesso in lettura non è stato salvato. Riprova.',
    readingAutoBodyThisServer:
      'Poneva legge da questo server senza chiedere. Su questo server non cambia nulla.',
    readingAutoBodyAny: 'Poneva legge da {service} senza chiedere. In {service} non cambia nulla.',
    changesNoun: 'one:modifica|many:di modifiche|other:modifiche',
    tagChange: 'Modifica',
    tagRead: 'Lettura',
    rowInclude: 'Includi: {action}',
    rowWaiting: 'In attesa di te',
    rowRunning: 'In esecuzione…',
    rowDone: 'Fatto',
    rowSkipped: 'Saltato',
    rowDeclined: 'Rifiutato',
    rowExpired: 'Scaduto',
    rowFailed: 'Fallito',
    rowUnknown: 'Impossibile confermare. Non verrà ritentato.',
    outcomeApproved: 'Approvato: {actions}.',
    actionsNoun: 'one:azione|many:di azioni|other:azioni',
    outcomeDeclined: 'Non è stato inviato nulla a {service}.',
    outcomeExpired: 'Questa richiesta è scaduta. Non è stato inviato nulla a {service}.',
    toolReadAuto: 'Letto da {service} automaticamente',
    scopeLabel: 'Chi può usarlo',
    scopeCompanyCaution: 'Collega solo un account destinato all’uso aziendale.',
  },
  lt: {
    connectTitle: 'Prijungti paslaugą „{service}“',
    connectBody:
      'Nuspręskite dabar, ką Poneva gali daryti su šia paskyra. Vėliau tai galite pakeisti skiltyje „Jungtys“.',
    scopeMe: 'Tik aš',
    scopeMeBody: 'Šią paskyrą galite naudoti tik jūs. Bendrinant pokalbį ar Skill ji nebendrinama.',
    scopeCompany: 'Visi įmonėje „{company}“',
    scopeCompanyBody:
      'Ją gali naudoti visi įmonėje „{company}“. Ją valdo savininkai ir administratoriai.',
    readingLabel: 'Skaitymas',
    readingAuto: 'Automatiškai',
    readingAutoBody:
      'Poneva be klausimų skaito: {things}. Paslaugoje „{service}“ niekas nesikeičia.',
    readingAsk: 'Klausti kiekvieną kartą',
    readingAskBody: 'Kiekvieną skaitymą patvirtinate jūs.',
    readingList: 'Ką Poneva gali skaityti ({count})',
    readingNone: 'Poneva klausia prieš kiekvieną šios paskyros naudojimą.',
    readOnlyProvider: 'Prieiga tik skaityti',
    changesLabel: 'Pakeitimai',
    changesFixed:
      'Dėl pakeitimų Poneva visada klausia. Kiekvieną pakeitimą patvirtinate prieš jam įvykstant.',
    continueTo: 'Tęsti į „{service}“',
    summary: 'Skaito automatiškai · Dėl pakeitimų klausia',
    summaryAsk: 'Dėl skaitymo klausia · Dėl pakeitimų klausia',
    reviewNeeded: 'Peržiūrėkite skaitymo prieigą',
    cardWants: 'Poneva nori:',
    cardReadsFooter: 'Paslaugoje „{service}“ niekas nesikeičia.',
    cardChangesFooter: 'Patvirtinus Poneva atliks {changes} paslaugoje „{service}“.',
    approveCount: 'Patvirtinti {n}',
    dontAllow: 'Neleisti',
    expiresAt: 'Baigia galioti {time}',
    rowDetails: 'Išsamiau',
    cancel: 'Atšaukti',
    save: 'Išsaugoti',
    saving: 'Išsaugoma…',
    adminOnly: 'Tai pakeisti gali tik savininkai ir administratoriai.',
    saveFailed: 'Skaitymo prieiga neišsaugota. Bandykite dar kartą.',
    readingAutoBodyThisServer:
      'Poneva iš šio serverio skaito neklausiant. Šiame serveryje niekas nesikeičia.',
    readingAutoBodyAny:
      'Poneva be klausimų skaito iš paslaugos „{service}“. Paslaugoje „{service}“ niekas nesikeičia.',
    changesNoun: 'one:pakeitimą|few:pakeitimus|many:pakeitimo|other:pakeitimų',
    tagChange: 'Pakeitimas',
    tagRead: 'Skaitymas',
    rowInclude: 'Įtraukti: {action}',
    rowWaiting: 'Laukia jūsų',
    rowRunning: 'Vykdoma…',
    rowDone: 'Atlikta',
    rowSkipped: 'Praleista',
    rowDeclined: 'Atmesta',
    rowExpired: 'Baigėsi galiojimas',
    rowFailed: 'Nepavyko',
    rowUnknown: 'Nepavyko patvirtinti. Daugiau nebus bandoma.',
    outcomeApproved: 'Patvirtinta: {actions}.',
    actionsNoun: 'one:veiksmas|few:veiksmai|many:veiksmo|other:veiksmų',
    outcomeDeclined: 'Į paslaugą „{service}“ nieko neišsiųsta.',
    outcomeExpired: 'Šios užklausos galiojimas baigėsi. Į paslaugą „{service}“ nieko neišsiųsta.',
    toolReadAuto: 'Automatiškai perskaityta iš paslaugos „{service}“',
    scopeLabel: 'Kas gali ja naudotis',
    scopeCompanyCaution: 'Junkite tik paskyrą, skirtą įmonės naudojimui.',
  },
  lv: {
    connectTitle: 'Savienot pakalpojumu “{service}”',
    connectBody:
      'Izlemiet tagad, ko Poneva drīkst darīt ar šo kontu. Vēlāk to varat mainīt sadaļā “Savienojumi”.',
    scopeMe: 'Tikai es',
    scopeMeBody:
      'Šo kontu varat izmantot tikai jūs. Kopīgojot tērzēšanu vai Skill, konts netiek kopīgots.',
    scopeCompany: 'Visi uzņēmumā “{company}”',
    scopeCompanyBody:
      'To var izmantot visi uzņēmumā “{company}”. To pārvalda īpašnieki un administratori.',
    readingLabel: 'Lasīšana',
    readingAuto: 'Automātiski',
    readingAutoBody:
      'Poneva bez jautāšanas lasa: {things}. Pakalpojumā “{service}” nekas nemainās.',
    readingAsk: 'Jautāt katru reizi',
    readingAskBody: 'Jūs apstiprināt katru lasīšanu.',
    readingList: 'Ko Poneva var lasīt ({count})',
    readingNone: 'Poneva jautā pirms katras šī konta izmantošanas reizes.',
    readOnlyProvider: 'Tikai lasīšanas piekļuve',
    changesLabel: 'Izmaiņas',
    changesFixed:
      'Par izmaiņām Poneva vienmēr jautā. Jūs apstiprināt katru izmaiņu, pirms tā notiek.',
    continueTo: 'Turpināt uz “{service}”',
    summary: 'Lasa automātiski · Par izmaiņām jautā',
    summaryAsk: 'Par lasīšanu jautā · Par izmaiņām jautā',
    reviewNeeded: 'Pārskatiet lasīšanas piekļuvi',
    cardWants: 'Poneva vēlas:',
    cardReadsFooter: 'Pakalpojumā “{service}” nekas nemainās.',
    cardChangesFooter: 'Apstiprinot Poneva veiks {changes} pakalpojumā “{service}”.',
    approveCount: 'Apstiprināt {n}',
    dontAllow: 'Neatļaut',
    expiresAt: 'Beidzas {time}',
    rowDetails: 'Informācija',
    cancel: 'Atcelt',
    save: 'Saglabāt',
    saving: 'Saglabā…',
    adminOnly: 'To var mainīt tikai īpašnieki un administratori.',
    saveFailed: 'Lasīšanas piekļuve netika saglabāta. Mēģiniet vēlreiz.',
    readingAutoBodyThisServer:
      'Poneva bez jautāšanas lasa no šī servera. Šajā serverī nekas nemainās.',
    readingAutoBodyAny:
      'Poneva bez jautāšanas lasa no pakalpojuma “{service}”. Pakalpojumā “{service}” nekas nemainās.',
    changesNoun: 'zero:izmaiņu|one:izmaiņu|other:izmaiņas',
    tagChange: 'Izmaiņa',
    tagRead: 'Lasīšana',
    rowInclude: 'Iekļaut: {action}',
    rowWaiting: 'Gaida jūs',
    rowRunning: 'Tiek izpildīts…',
    rowDone: 'Pabeigts',
    rowSkipped: 'Izlaists',
    rowDeclined: 'Noraidīts',
    rowExpired: 'Beidzies',
    rowFailed: 'Neizdevās',
    rowUnknown: 'Neizdevās apstiprināt. Tas netiks mēģināts vēlreiz.',
    outcomeApproved: 'Apstiprināts: {actions}.',
    actionsNoun: 'zero:darbību|one:darbība|other:darbības',
    outcomeDeclined: 'Uz pakalpojumu “{service}” nekas netika nosūtīts.',
    outcomeExpired:
      'Šī pieprasījuma derīgums beidzās. Uz pakalpojumu “{service}” nekas netika nosūtīts.',
    toolReadAuto: 'Automātiski nolasīts no pakalpojuma “{service}”',
    scopeLabel: 'Kas to var izmantot',
    scopeCompanyCaution: 'Savienojiet tikai kontu, kas paredzēts uzņēmuma lietošanai.',
  },
  mt: {
    connectTitle: 'Qabbad {service}',
    connectBody:
      'Iddeċiedi issa x’jista’ jagħmel Poneva b’dan il-kont. Tista’ tibdlu aktar tard f’Konnessjonijiet.',
    scopeMe: 'Jien biss',
    scopeMeBody:
      'Int biss tista’ tuża dan il-kont. Jekk taqsam chat jew Skill, il-kont ma jinqasamx.',
    scopeCompany: 'Kulħadd f’{company}',
    scopeCompanyBody:
      'Kulħadd f’{company} jista’ jużah. Jimmaniġġjawh is-sidien u l-amministraturi.',
    readingLabel: 'Qari',
    readingAuto: 'Awtomatikament',
    readingAutoBody: 'Poneva jaqra {things} mingħajr ma jistaqsi. Xejn ma jinbidel f’{service}.',
    readingAsk: 'Staqsi kull darba',
    readingAskBody: 'Int tapprova kull qari.',
    readingList: 'X’jista’ jaqra Poneva ({count})',
    readingNone: 'Poneva jistaqsi qabel kull użu ta’ dan il-kont.',
    readOnlyProvider: 'Aċċess għall-qari biss',
    changesLabel: 'Bidliet',
    changesFixed: 'Għall-bidliet Poneva dejjem jistaqsi. Int tapprova kull bidla qabel ma ssir.',
    continueTo: 'Kompli lejn {service}',
    summary: 'Jaqra awtomatikament · Jistaqsi għall-bidliet',
    summaryAsk: 'Jistaqsi għall-qari · Jistaqsi għall-bidliet',
    reviewNeeded: 'Irrevedi l-aċċess għall-qari',
    cardWants: 'Poneva jrid:',
    cardReadsFooter: 'Xejn ma jinbidel f’{service}.',
    cardChangesFooter: 'Jekk tapprova, Poneva jagħmel {changes} f’{service}.',
    approveCount: 'Approva {n}',
    dontAllow: 'Tippermettix',
    expiresAt: 'Jiskadi {time}',
    rowDetails: 'Dettalji',
    cancel: 'Ikkanċella',
    save: 'Issejvja',
    saving: 'Qed jissejvja…',
    adminOnly: 'Is-sidien u l-amministraturi biss jistgħu jibdlu dan.',
    saveFailed: 'L-aċċess għall-qari ma ġiex issejvjat. Erġa’ pprova.',
    readingAutoBodyThisServer:
      'Poneva jaqra minn dan is-server mingħajr ma jistaqsi. Xejn fuq dan is-server ma jinbidel.',
    readingAutoBodyAny:
      'Poneva jaqra minn {service} mingħajr ma jistaqsi. Xejn ma jinbidel f’{service}.',
    changesNoun: 'one:bidla|two:bidliet|few:bidliet|many:bidla|other:bidla',
    tagChange: 'Bidla',
    tagRead: 'Qari',
    rowInclude: 'Inkludi: {action}',
    rowWaiting: 'Qed jistennik',
    rowRunning: 'Għaddej…',
    rowDone: 'Lest',
    rowSkipped: 'Maqbuż',
    rowDeclined: 'Miċħud',
    rowExpired: 'Skada',
    rowFailed: 'Falla',
    rowUnknown: 'Ma setax jiġi kkonfermat. Mhux se jerġa’ jiġi pprovat.',
    outcomeApproved: 'Approvat: {actions}.',
    actionsNoun: 'one:azzjoni|two:azzjonijiet|few:azzjonijiet|many:azzjoni|other:azzjoni',
    outcomeDeclined: 'Xejn ma ntbagħat lil {service}.',
    outcomeExpired: 'Din it-talba skadiet. Xejn ma ntbagħat lil {service}.',
    toolReadAuto: 'Inqara minn {service} awtomatikament',
    scopeLabel: 'Min jista’ jużah',
    scopeCompanyCaution: 'Qabbad biss kont maħsub għall-użu tal-kumpanija.',
  },
  nl: {
    connectTitle: '{service} verbinden',
    connectBody:
      'Bepaal nu wat Poneva met dit account mag doen. Je kunt dit later wijzigen in Verbindingen.',
    scopeMe: 'Alleen ik',
    scopeMeBody:
      'Alleen jij kunt dit account gebruiken. Als je een chat of Skill deelt, wordt het niet gedeeld.',
    scopeCompany: 'Iedereen bij {company}',
    scopeCompanyBody:
      'Iedereen bij {company} kan het gebruiken. Eigenaren en beheerders beheren het.',
    readingLabel: 'Lezen',
    readingAuto: 'Automatisch',
    readingAutoBody: 'Poneva leest {things} zonder te vragen. Er verandert niets in {service}.',
    readingAsk: 'Elke keer vragen',
    readingAskBody: 'Je keurt elke leesactie goed.',
    readingList: 'Wat Poneva kan lezen ({count})',
    readingNone: 'Poneva vraagt het voor elk gebruik van dit account.',
    readOnlyProvider: 'Alleen-lezentoegang',
    changesLabel: 'Wijzigingen',
    changesFixed:
      'Bij wijzigingen vraagt Poneva het altijd. Je keurt elke wijziging goed voordat die gebeurt.',
    continueTo: 'Doorgaan naar {service}',
    summary: 'Leest automatisch · Vraagt bij wijzigingen',
    summaryAsk: 'Vraagt bij lezen · Vraagt bij wijzigingen',
    reviewNeeded: 'Leestoegang controleren',
    cardWants: 'Poneva wil:',
    cardReadsFooter: 'Er verandert niets in {service}.',
    cardChangesFooter: 'Als je goedkeurt, voert Poneva {changes} door in {service}.',
    approveCount: '{n} goedkeuren',
    dontAllow: 'Niet toestaan',
    expiresAt: 'Verloopt {time}',
    rowDetails: 'Details',
    cancel: 'Annuleren',
    save: 'Opslaan',
    saving: 'Opslaan…',
    adminOnly: 'Alleen eigenaren en beheerders kunnen dit wijzigen.',
    saveFailed: 'De leestoegang is niet opgeslagen. Probeer het opnieuw.',
    readingAutoBodyThisServer:
      'Poneva leest van deze server zonder te vragen. Er verandert niets op deze server.',
    readingAutoBodyAny:
      'Poneva leest uit {service} zonder te vragen. Er verandert niets in {service}.',
    changesNoun: 'one:wijziging|other:wijzigingen',
    tagChange: 'Wijziging',
    tagRead: 'Lezen',
    rowInclude: 'Opnemen: {action}',
    rowWaiting: 'Wacht op jou',
    rowRunning: 'Wordt uitgevoerd…',
    rowDone: 'Klaar',
    rowSkipped: 'Overgeslagen',
    rowDeclined: 'Geweigerd',
    rowExpired: 'Verlopen',
    rowFailed: 'Mislukt',
    rowUnknown: 'Kon niet worden bevestigd. Er wordt niet opnieuw geprobeerd.',
    outcomeApproved: 'Goedgekeurd: {actions}.',
    actionsNoun: 'one:actie|other:acties',
    outcomeDeclined: 'Er is niets naar {service} verzonden.',
    outcomeExpired: 'Dit verzoek is verlopen. Er is niets naar {service} verzonden.',
    toolReadAuto: 'Automatisch gelezen uit {service}',
    scopeLabel: 'Wie het kan gebruiken',
    scopeCompanyCaution: 'Koppel alleen een account dat bedoeld is voor zakelijk gebruik.',
  },
  pl: {
    connectTitle: 'Połącz usługę {service}',
    connectBody:
      'Zdecyduj teraz, co Poneva może robić z tym kontem. Możesz to później zmienić w sekcji Połączenia.',
    scopeMe: 'Tylko ja',
    scopeMeBody:
      'Tylko ty możesz używać tego konta. Udostępnienie czatu lub Skilla nie udostępnia konta.',
    scopeCompany: 'Wszyscy w firmie {company}',
    scopeCompanyBody:
      'Wszyscy w firmie {company} mogą go używać. Zarządzają nim właściciele i administratorzy.',
    readingLabel: 'Odczyt',
    readingAuto: 'Automatycznie',
    readingAutoBody:
      'Poneva odczytuje {things} bez pytania. W usłudze {service} nic się nie zmienia.',
    readingAsk: 'Pytaj za każdym razem',
    readingAskBody: 'Zatwierdzasz każdy odczyt.',
    readingList: 'Co Poneva może odczytać ({count})',
    readingNone: 'Poneva pyta przed każdym użyciem tego konta.',
    readOnlyProvider: 'Dostęp tylko do odczytu',
    changesLabel: 'Zmiany',
    changesFixed: 'O zmiany Poneva zawsze pyta. Zatwierdzasz każdą zmianę, zanim nastąpi.',
    continueTo: 'Przejdź do usługi {service}',
    summary: 'Odczytuje automatycznie · Pyta o zmiany',
    summaryAsk: 'Pyta o odczyt · Pyta o zmiany',
    reviewNeeded: 'Sprawdź dostęp do odczytu',
    cardWants: 'Poneva chce:',
    cardReadsFooter: 'W usłudze {service} nic się nie zmienia.',
    cardChangesFooter: 'Po zatwierdzeniu Poneva wprowadzi {changes} w usłudze {service}.',
    approveCount: 'Zatwierdź {n}',
    dontAllow: 'Nie zezwalaj',
    expiresAt: 'Wygasa {time}',
    rowDetails: 'Szczegóły',
    cancel: 'Anuluj',
    save: 'Zapisz',
    saving: 'Zapisywanie…',
    adminOnly: 'Tylko właściciele i administratorzy mogą to zmienić.',
    saveFailed: 'Nie zapisano dostępu do odczytu. Spróbuj ponownie.',
    readingAutoBodyThisServer:
      'Poneva odczytuje dane z tego serwera bez pytania. Na tym serwerze nic się nie zmienia.',
    readingAutoBodyAny:
      'Poneva odczytuje dane z usługi {service} bez pytania. W usłudze {service} nic się nie zmienia.',
    changesNoun: 'one:zmianę|few:zmiany|many:zmian|other:zmiany',
    tagChange: 'Zmiana',
    tagRead: 'Odczyt',
    rowInclude: 'Uwzględnij: {action}',
    rowWaiting: 'Czeka na ciebie',
    rowRunning: 'W toku…',
    rowDone: 'Gotowe',
    rowSkipped: 'Pominięte',
    rowDeclined: 'Odrzucone',
    rowExpired: 'Wygasłe',
    rowFailed: 'Nieudane',
    rowUnknown: 'Nie udało się potwierdzić. Nie zostanie ponowione.',
    outcomeApproved: 'Zatwierdzone: {actions}.',
    actionsNoun: 'one:działanie|few:działania|many:działań|other:działania',
    outcomeDeclined: 'Do usługi {service} nic nie wysłano.',
    outcomeExpired: 'Ta prośba wygasła. Do usługi {service} nic nie wysłano.',
    toolReadAuto: 'Automatycznie odczytano z usługi {service}',
    scopeLabel: 'Kto może go używać',
    scopeCompanyCaution: 'Łącz tylko konto przeznaczone do użytku firmowego.',
  },
  pt: {
    connectTitle: 'Ligar {service}',
    connectBody:
      'Decida agora o que o Poneva pode fazer com esta conta. Pode alterar isto mais tarde em Ligações.',
    scopeMe: 'Só eu',
    scopeMeBody: 'Esta conta é só sua. Partilhar uma conversa ou um Skill não a partilha.',
    scopeCompany: 'Todos em {company}',
    scopeCompanyBody:
      'Todos em {company} podem usá-la. É gerida pelos proprietários e administradores.',
    readingLabel: 'Leitura',
    readingAuto: 'Automaticamente',
    readingAutoBody: 'O Poneva lê {things} sem perguntar. Nada muda em {service}.',
    readingAsk: 'Perguntar sempre',
    readingAskBody: 'Aprova cada leitura.',
    readingList: 'O que o Poneva pode ler ({count})',
    readingNone: 'O Poneva pergunta antes de cada utilização desta conta.',
    readOnlyProvider: 'Acesso só de leitura',
    changesLabel: 'Alterações',
    changesFixed:
      'Para alterações, o Poneva pergunta sempre. Aprova cada alteração antes de ser feita.',
    continueTo: 'Continuar para {service}',
    summary: 'Lê automaticamente · Pergunta antes de alterar',
    summaryAsk: 'Pergunta antes de ler · Pergunta antes de alterar',
    reviewNeeded: 'Rever o acesso de leitura',
    cardWants: 'O Poneva quer:',
    cardReadsFooter: 'Nada muda em {service}.',
    cardChangesFooter: 'Ao aprovar, o Poneva faz {changes} em {service}.',
    approveCount: 'Aprovar {n}',
    dontAllow: 'Não permitir',
    expiresAt: 'Expira {time}',
    rowDetails: 'Detalhes',
    cancel: 'Cancelar',
    save: 'Guardar',
    saving: 'A guardar…',
    adminOnly: 'Só os proprietários e administradores podem alterar isto.',
    saveFailed: 'O acesso de leitura não foi guardado. Tente novamente.',
    readingAutoBodyThisServer:
      'O Poneva lê deste servidor sem perguntar. Nada muda neste servidor.',
    readingAutoBodyAny: 'O Poneva lê de {service} sem perguntar. Nada muda em {service}.',
    changesNoun: 'one:alteração|many:de alterações|other:alterações',
    tagChange: 'Alteração',
    tagRead: 'Leitura',
    rowInclude: 'Incluir: {action}',
    rowWaiting: 'A aguardar por si',
    rowRunning: 'Em execução…',
    rowDone: 'Concluído',
    rowSkipped: 'Ignorado',
    rowDeclined: 'Recusado',
    rowExpired: 'Expirado',
    rowFailed: 'Falhou',
    rowUnknown: 'Não foi possível confirmar. Não será tentado novamente.',
    outcomeApproved: 'Aprovado: {actions}.',
    actionsNoun: 'one:ação|many:de ações|other:ações',
    outcomeDeclined: 'Nada foi enviado para {service}.',
    outcomeExpired: 'Este pedido expirou. Nada foi enviado para {service}.',
    toolReadAuto: 'Lido de {service} automaticamente',
    scopeLabel: 'Quem a pode usar',
    scopeCompanyCaution: 'Ligue apenas uma conta destinada a uso da empresa.',
  },
  ro: {
    connectTitle: 'Conectează {service}',
    connectBody:
      'Decide acum ce poate face Poneva cu acest cont. Poți schimba asta mai târziu în Conexiuni.',
    scopeMe: 'Doar eu',
    scopeMeBody:
      'Doar tu poți folosi acest cont. Dacă partajezi un chat sau un Skill, contul nu este partajat.',
    scopeCompany: 'Toată lumea din {company}',
    scopeCompanyBody:
      'Toată lumea din {company} îl poate folosi. Îl gestionează proprietarii și administratorii.',
    readingLabel: 'Citire',
    readingAuto: 'Automat',
    readingAutoBody: 'Poneva citește {things} fără să întrebe. Nimic nu se schimbă în {service}.',
    readingAsk: 'Întreabă de fiecare dată',
    readingAskBody: 'Aprobi fiecare citire.',
    readingList: 'Ce poate citi Poneva ({count})',
    readingNone: 'Poneva întreabă înainte de fiecare utilizare a acestui cont.',
    readOnlyProvider: 'Acces doar pentru citire',
    changesLabel: 'Modificări',
    changesFixed:
      'Pentru modificări, Poneva întreabă mereu. Aprobi fiecare modificare înainte să aibă loc.',
    continueTo: 'Continuă la {service}',
    summary: 'Citește automat · Întreabă pentru modificări',
    summaryAsk: 'Întreabă pentru citire · Întreabă pentru modificări',
    reviewNeeded: 'Verifică accesul de citire',
    cardWants: 'Poneva vrea să:',
    cardReadsFooter: 'Nimic nu se schimbă în {service}.',
    cardChangesFooter: 'Dacă aprobi, Poneva face {changes} în {service}.',
    approveCount: 'Aprobă {n}',
    dontAllow: 'Nu permite',
    expiresAt: 'Expiră {time}',
    rowDetails: 'Detalii',
    cancel: 'Anulează',
    save: 'Salvează',
    saving: 'Se salvează…',
    adminOnly: 'Doar proprietarii și administratorii pot modifica acest lucru.',
    saveFailed: 'Accesul de citire nu a fost salvat. Încearcă din nou.',
    readingAutoBodyThisServer:
      'Poneva citește de pe acest server fără să întrebe. Nimic nu se schimbă pe acest server.',
    readingAutoBodyAny:
      'Poneva citește din {service} fără să întrebe. Nimic nu se schimbă în {service}.',
    changesNoun: 'one:modificare|few:modificări|other:de modificări',
    tagChange: 'Modificare',
    tagRead: 'Citire',
    rowInclude: 'Include: {action}',
    rowWaiting: 'Te așteaptă',
    rowRunning: 'În execuție…',
    rowDone: 'Gata',
    rowSkipped: 'Omis',
    rowDeclined: 'Refuzat',
    rowExpired: 'Expirat',
    rowFailed: 'Eșuat',
    rowUnknown: 'Nu s-a putut confirma. Nu se va încerca din nou.',
    outcomeApproved: 'Aprobat: {actions}.',
    actionsNoun: 'one:acțiune|few:acțiuni|other:de acțiuni',
    outcomeDeclined: 'Nu s-a trimis nimic către {service}.',
    outcomeExpired: 'Această solicitare a expirat. Nu s-a trimis nimic către {service}.',
    toolReadAuto: 'Citit automat din {service}',
    scopeLabel: 'Cine îl poate folosi',
    scopeCompanyCaution: 'Conectați doar un cont destinat utilizării în companie.',
  },
  sk: {
    connectTitle: 'Pripojiť službu {service}',
    connectBody:
      'Rozhodnite teraz, čo smie Poneva s týmto účtom robiť. Neskôr to môžete zmeniť v sekcii Pripojenia.',
    scopeMe: 'Len ja',
    scopeMeBody: 'Tento účet môžete používať len vy. Zdieľaním četu alebo Skillu sa účet nezdieľa.',
    scopeCompany: 'Všetci vo firme {company}',
    scopeCompanyBody:
      'Môžu ho používať všetci vo firme {company}. Spravujú ho vlastníci a správcovia.',
    readingLabel: 'Čítanie',
    readingAuto: 'Automaticky',
    readingAutoBody: 'Poneva číta {things} bez pýtania. V službe {service} sa nič nezmení.',
    readingAsk: 'Zakaždým sa opýtať',
    readingAskBody: 'Každé čítanie schvaľujete.',
    readingList: 'Čo môže Poneva čítať ({count})',
    readingNone: 'Poneva sa opýta pred každým použitím tohto účtu.',
    readOnlyProvider: 'Prístup len na čítanie',
    changesLabel: 'Zmeny',
    changesFixed: 'Na zmeny sa Poneva vždy opýta. Každú zmenu schvaľujete skôr, než sa vykoná.',
    continueTo: 'Pokračovať do služby {service}',
    summary: 'Číta automaticky · Na zmeny sa pýta',
    summaryAsk: 'Na čítanie sa pýta · Na zmeny sa pýta',
    reviewNeeded: 'Skontrolujte prístup na čítanie',
    cardWants: 'Poneva chce:',
    cardReadsFooter: 'V službe {service} sa nič nezmení.',
    cardChangesFooter: 'Schválením umožníte Poneva vykonať {changes} v službe {service}.',
    approveCount: 'Schváliť {n}',
    dontAllow: 'Nepovoliť',
    expiresAt: 'Vyprší {time}',
    rowDetails: 'Podrobnosti',
    cancel: 'Zrušiť',
    save: 'Uložiť',
    saving: 'Ukladá sa…',
    adminOnly: 'Zmeniť to môžu iba vlastníci a správcovia.',
    saveFailed: 'Prístup na čítanie sa neuložil. Skúste to znova.',
    readingAutoBodyThisServer:
      'Poneva číta z tohto servera bez pýtania. Na tomto serveri sa nič nemení.',
    readingAutoBodyAny:
      'Poneva číta zo služby {service} bez pýtania. V službe {service} sa nič nezmení.',
    changesNoun: 'one:zmenu|few:zmeny|many:zmeny|other:zmien',
    tagChange: 'Zmena',
    tagRead: 'Čítanie',
    rowInclude: 'Zahrnúť: {action}',
    rowWaiting: 'Čaká na vás',
    rowRunning: 'Prebieha…',
    rowDone: 'Hotovo',
    rowSkipped: 'Preskočené',
    rowDeclined: 'Odmietnuté',
    rowExpired: 'Platnosť vypršala',
    rowFailed: 'Zlyhalo',
    rowUnknown: 'Nepodarilo sa overiť. Znova sa to neskúsi.',
    outcomeApproved: 'Schválené: {actions}.',
    actionsNoun: 'one:akcia|few:akcie|many:akcie|other:akcií',
    outcomeDeclined: 'Do služby {service} sa nič neodoslalo.',
    outcomeExpired: 'Platnosť žiadosti vypršala. Do služby {service} sa nič neodoslalo.',
    toolReadAuto: 'Automaticky prečítané zo služby {service}',
    scopeLabel: 'Kto ho môže používať',
    scopeCompanyCaution: 'Pripojte iba účet určený na firemné použitie.',
  },
  sl: {
    connectTitle: 'Poveži storitev {service}',
    connectBody:
      'Zdaj se odločite, kaj sme Poneva početi s tem računom. Pozneje lahko to spremenite v razdelku Povezave.',
    scopeMe: 'Samo jaz',
    scopeMeBody:
      'Ta račun lahko uporabljate samo vi. Z deljenjem klepeta ali Skilla se račun ne deli.',
    scopeCompany: 'Vsi v podjetju {company}',
    scopeCompanyBody:
      'Uporabljajo ga lahko vsi v podjetju {company}. Upravljajo ga lastniki in skrbniki.',
    readingLabel: 'Branje',
    readingAuto: 'Samodejno',
    readingAutoBody:
      'Poneva brez vprašanja bere: {things}. V storitvi {service} se nič ne spremeni.',
    readingAsk: 'Vsakič vprašaj',
    readingAskBody: 'Vsako branje odobrite.',
    readingList: 'Kaj lahko bere Poneva ({count})',
    readingNone: 'Poneva vpraša pred vsako uporabo tega računa.',
    readOnlyProvider: 'Dostop samo za branje',
    changesLabel: 'Spremembe',
    changesFixed: 'Za spremembe Poneva vedno vpraša. Vsako spremembo odobrite, preden se zgodi.',
    continueTo: 'Nadaljuj v storitev {service}',
    summary: 'Bere samodejno · Za spremembe vpraša',
    summaryAsk: 'Za branje vpraša · Za spremembe vpraša',
    reviewNeeded: 'Preglejte dostop za branje',
    cardWants: 'Poneva želi:',
    cardReadsFooter: 'V storitvi {service} se nič ne spremeni.',
    cardChangesFooter: 'Z odobritvijo dovolite, da Poneva naredi {changes} v storitvi {service}.',
    approveCount: 'Odobri {n}',
    dontAllow: 'Ne dovoli',
    expiresAt: 'Poteče {time}',
    rowDetails: 'Podrobnosti',
    cancel: 'Prekliči',
    save: 'Shrani',
    saving: 'Shranjevanje…',
    adminOnly: 'To lahko spremenijo samo lastniki in skrbniki.',
    saveFailed: 'Dostop za branje ni bil shranjen. Poskusite znova.',
    readingAutoBodyThisServer:
      'Poneva bere s tega strežnika brez vprašanja. Na tem strežniku se nič ne spremeni.',
    readingAutoBodyAny:
      'Poneva brez vprašanja bere iz storitve {service}. V storitvi {service} se nič ne spremeni.',
    changesNoun: 'one:spremembo|two:spremembi|few:spremembe|other:sprememb',
    tagChange: 'Sprememba',
    tagRead: 'Branje',
    rowInclude: 'Vključi: {action}',
    rowWaiting: 'Čaka na vas',
    rowRunning: 'V izvajanju…',
    rowDone: 'Končano',
    rowSkipped: 'Preskočeno',
    rowDeclined: 'Zavrnjeno',
    rowExpired: 'Poteklo',
    rowFailed: 'Neuspešno',
    rowUnknown: 'Ni bilo mogoče potrditi. Ne bo ponovljeno.',
    outcomeApproved: 'Odobreno: {actions}.',
    actionsNoun: 'one:dejanje|two:dejanji|few:dejanja|other:dejanj',
    outcomeDeclined: 'V storitev {service} ni bilo poslano nič.',
    outcomeExpired: 'Ta zahteva je potekla. V storitev {service} ni bilo poslano nič.',
    toolReadAuto: 'Samodejno prebrano iz storitve {service}',
    scopeLabel: 'Kdo ga lahko uporablja',
    scopeCompanyCaution: 'Povežite samo račun, ki je namenjen poslovni rabi.',
  },
  sv: {
    connectTitle: 'Anslut {service}',
    connectBody:
      'Bestäm nu vad Poneva får göra med det här kontot. Du kan ändra det senare under Anslutningar.',
    scopeMe: 'Bara jag',
    scopeMeBody:
      'Bara du kan använda det här kontot. Det delas inte när du delar en chatt eller Skill.',
    scopeCompany: 'Alla på {company}',
    scopeCompanyBody: 'Alla på {company} kan använda det. Ägare och administratörer hanterar det.',
    readingLabel: 'Läsning',
    readingAuto: 'Automatiskt',
    readingAutoBody: 'Poneva läser {things} utan att fråga. Inget i {service} ändras.',
    readingAsk: 'Fråga varje gång',
    readingAskBody: 'Du godkänner varje läsning.',
    readingList: 'Vad Poneva kan läsa ({count})',
    readingNone: 'Poneva frågar före varje användning av det här kontot.',
    readOnlyProvider: 'Bara läsåtkomst',
    changesLabel: 'Ändringar',
    changesFixed: 'Poneva frågar alltid före ändringar. Du godkänner varje ändring innan den görs.',
    continueTo: 'Fortsätt till {service}',
    summary: 'Läser automatiskt · Frågar vid ändringar',
    summaryAsk: 'Frågar vid läsning · Frågar vid ändringar',
    reviewNeeded: 'Granska läsåtkomst',
    cardWants: 'Poneva vill:',
    cardReadsFooter: 'Inget i {service} ändras.',
    cardChangesFooter: 'Om du godkänner gör Poneva {changes} i {service}.',
    approveCount: 'Godkänn {n}',
    dontAllow: 'Tillåt inte',
    expiresAt: 'Går ut {time}',
    rowDetails: 'Detaljer',
    cancel: 'Avbryt',
    save: 'Spara',
    saving: 'Sparar…',
    adminOnly: 'Bara ägare och administratörer kan ändra det här.',
    saveFailed: 'Läsåtkomsten sparades inte. Försök igen.',
    readingAutoBodyThisServer:
      'Poneva läser från den här servern utan att fråga. Inget på den här servern ändras.',
    readingAutoBodyAny: 'Poneva läser från {service} utan att fråga. Inget i {service} ändras.',
    changesNoun: 'one:ändring|other:ändringar',
    tagChange: 'Ändring',
    tagRead: 'Läsning',
    rowInclude: 'Ta med: {action}',
    rowWaiting: 'Väntar på dig',
    rowRunning: 'Körs…',
    rowDone: 'Klar',
    rowSkipped: 'Överhoppad',
    rowDeclined: 'Avböjd',
    rowExpired: 'Utgången',
    rowFailed: 'Misslyckad',
    rowUnknown: 'Kunde inte bekräftas. Inget nytt försök görs.',
    outcomeApproved: 'Godkänd: {actions}.',
    actionsNoun: 'one:åtgärd|other:åtgärder',
    outcomeDeclined: 'Inget skickades till {service}.',
    outcomeExpired: 'Begäran har gått ut. Inget skickades till {service}.',
    toolReadAuto: 'Läst från {service} automatiskt',
    scopeLabel: 'Vem kan använda det',
    scopeCompanyCaution: 'Anslut bara ett konto som är avsett för företagsbruk.',
  },
};

const languages = [
  'en',
  'bg',
  'cs',
  'da',
  'de',
  'el',
  'es',
  'et',
  'fi',
  'fr',
  'ga',
  'hr',
  'hu',
  'it',
  'lt',
  'lv',
  'mt',
  'nl',
  'pl',
  'pt',
  'ro',
  'sk',
  'sl',
  'sv',
] as const;

const copy = Object.fromEntries(
  languages.map((language) => [language, language === 'en' ? { ...en } : { ...others[language] }]),
) as NamespaceCatalog<ConnectAccessCopyKey>;

export default copy;
